import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface SuspensionInfo {
  isSuspended: boolean;
  suspensionType?: string;
  reason?: string;
  expiresAt?: string;
  isPermanent: boolean;
}

export function useSuspensionCheck() {
  const { user } = useAuth();
  const [suspensionInfo, setSuspensionInfo] = useState<SuspensionInfo>({
    isSuspended: false,
    isPermanent: false,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    checkSuspension();
  }, [user]);

  const checkSuspension = async () => {
    if (!user) return;

    try {
      setLoading(true);
      
      // Check if user is currently suspended
      const { data: isSuspended } = await supabase
        .rpc('is_user_suspended', { check_user_id: user.id });

      if (isSuspended) {
        // Get suspension details
        const { data: suspension } = await supabase
          .from('user_suspensions')
          .select('suspension_type, reason, expires_at')
          .eq('user_id', user.id)
          .eq('status', 'active')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (suspension) {
          setSuspensionInfo({
            isSuspended: true,
            suspensionType: suspension.suspension_type,
            reason: suspension.reason,
            expiresAt: suspension.expires_at,
            isPermanent: suspension.suspension_type === 'permanent',
          });
        }
      } else {
        setSuspensionInfo({
          isSuspended: false,
          isPermanent: false,
        });
      }
    } catch (error) {
      console.error('Error checking suspension:', error);
    } finally {
      setLoading(false);
    }
  };

  return {
    suspensionInfo,
    loading,
    checkSuspension,
  };
}